import { Tooltip, TooltipContent, TooltipTrigger } from "@exopengithub/exokit";
import { IconArrowNarrowRight, IconCirclePlus } from "@tabler/icons-react";
import { EntityPickerField } from "molecules/entity-picker/EntityPickerField";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router";
import { AddCompany } from "src/features/companyAdministration/AddCompany.js";
import { useLastActiveContext } from "src/common/utils/hooks/useLastActiveContext.js";
import { ALL_COMPANY_DOMAINS } from "src/common/utils/lastActiveContext.js";
import {
	useLoadedCompanyDomain,
	useLoadedCompanyDomains,
} from "src/routes/routeDataHooks.js";
import { useRouteTree } from "src/routes/routeTreeContext.js";
import { buildScopedNavigationPath } from "./scopedFeaturePaths.js";
import { SCOPE_SWITCH_STATE } from "src/routes/scopeChange.js";
import { ALL_LEGAL_ENTITIES } from "src/common/utils/lastActiveContext.js";
import { DOMAIN_TERMS } from "src/common/domainTerms.js";

interface CompanySelectProps {
	/** Superadmin — gates the "new company domain" action, independent of dev-tools unlock. */
	canCreateCompanyDomain?: boolean;
	/** Superadmin — gates the "All domains" option. */
	canSelectAllDomains?: boolean;
}

export const CompanySelect = ({
	canCreateCompanyDomain = false,
	canSelectAllDomains = false,
}: CompanySelectProps) => {
	const { t } = useTranslation();
	const companyDomains = useLoadedCompanyDomains();
	const currentCompanyDomain = useLoadedCompanyDomain();
	const { write: writeLastActiveContext } = useLastActiveContext();
	const { pathname } = useLocation();
	const { routeObjects, gatedFeatureSegments } = useRouteTree();
	const items = useMemo(() => {
		const companyList = companyDomains
			.filter(
				(companyDomain) => `${companyDomain.id}` !== currentCompanyDomain.id,
			)
			.map((companyDomain) => ({
				id: `${companyDomain.id}`,
				name: companyDomain.name,
			}));
		companyList.sort((a, b) => a.name.localeCompare(b.name));
		if (canSelectAllDomains) {
			return [
				{ id: ALL_COMPANY_DOMAINS, name: t("All domains") },
				...companyList,
			];
		}
		return companyList;
	}, [companyDomains, currentCompanyDomain.id, canSelectAllDomains, t]);

	const hasSomewhereToGo = items.length > 0 || canCreateCompanyDomain;

	return (
		<EntityPickerField
			data-experience-anchor="domain-picker"
			data-testid="company-select"
			interactive={hasSomewhereToGo}
			pillLabel={t(DOMAIN_TERMS.DOMAIN)}
			currentLabel={currentCompanyDomain.name}
			items={items}
			searchPlaceholder={t("Search company domain")}
			renderHeaderAction={({ close }) =>
				canCreateCompanyDomain && (
					<AddCompany onCreated={close}>
						{(triggerProps) => (
							<Tooltip placement="top">
								<TooltipTrigger asChild>
									<button
										type="button"
										{...triggerProps}
										aria-label={t("New Company Domain")}
										className="shrink-0 rounded-full p-1 text-primary-800 hover:bg-gray-100"
									>
										<IconCirclePlus size={20} strokeWidth={1.5} />
									</button>
								</TooltipTrigger>
								<TooltipContent>{t("New Company Domain")}</TooltipContent>
							</Tooltip>
						)}
					</AddCompany>
				)
			}
			renderItem={(company, { close }) => {
				const isAllDomains = company.id === ALL_COMPANY_DOMAINS;
				return (
					<Link
						to={
							isAllDomains
								? "/"
								: buildScopedNavigationPath({
										pathname,
										companyDomainId: company.id,
										routeObjects,
										gatedFeatureSegments,
									})
						}
						state={SCOPE_SWITCH_STATE}
						className="group flex items-center gap-2 border-b px-3 py-2 text-gray-700 last:border-b-0 hover:bg-gray-100"
						onClick={() => {
							// Domain switch lands domain-wide, so record that scope explicitly — restore-on-login must not invent an entity.
							writeLastActiveContext({
								companyDomainId: company.id,
								legalEntityId: ALL_LEGAL_ENTITIES,
							});
							close();
						}}
					>
						<div className="grow truncate">{company.name}</div>
						<IconArrowNarrowRight
							className="shrink-0 text-gray-500 opacity-0 group-hover:opacity-100"
							size={16}
							strokeWidth={1.5}
						/>
					</Link>
				);
			}}
		/>
	);
};
